import { Component, OnInit, Input, Output, SimpleChanges, EventEmitter, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import { Subject } from "rxjs";
import { Subscription } from 'rxjs';
import { SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";

@Component({
    selector: 'recent-pays',
    templateUrl: './recent-pays.component.html',
    styleUrls: ['./recent-pays.component.scss'],
    standalone: false
})
export class RecentPaysComponent implements OnInit, OnDestroy {

    // **************************
    // ****** variables *********
    // **************************

    // Unique internal identity of the lot whose recent payments will be queried for.
    @Input() lotId: number = 0

    // Unique internal identity of the contact whose recent payments will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Unique internal identity of the sale finance type to filter the recent payments with.
    @Input() financeTypeId: number = 0

    // A payment was taken so lets update our available payments and any suppporting displays.
    @Input() paymentTaken: Subject<Object>

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // Communicate refund updates to the parent component.
    @Output() refundProcessed: EventEmitter<number> = new EventEmitter()

    //
    protected payments: Array<any> = []

    //
    protected records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    // A handle to a payment taken subscription so we can unsubscribe later.
    private paymentTakenWatcher: any = null

    // The receipt number search form group
    protected form: FormGroup | null = null

    // A receipt number to filter the list by.
    protected receiptNumber = null

    /** A debounce timer for search events. Used to wait for the user to stop typing before we fetch. */
    private watcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    // Unique internal identity of the receipt the user wants to view.
    protected receiptId: number = 0

    constructor (private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, private store: StoreService, private fb: FormBuilder) {
        this.form = this.fb.group({
            number: new FormControl<string>('', [])
        })
    }

    ngOnInit() {
        this.fetch()

        this.subscriptions.push(
            this.form.get('number').valueChanges.subscribe(number => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.receiptNumber === number) {
                    return
                }

                if (!/^[0-9]*$/g.test(number)) {
                    return
                }
        
                this.watcher = setTimeout(() => {
                    this.receiptNumber = number
                    this.reset()            
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        if (this.paymentTaken) {
            this.subscriptions.push(
                this.paymentTaken.subscribe((payment) => {
                    this.reset()
                    this.fetch()
                })
            )
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId || changes.contactId || changes.financeTypeId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let lotParam = ''
        if (this.lotId && (this.lotId > 0)) {
            lotParam = `&lot=${this.lotId}`
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
            lotParam = ''
        }

        let financeTypeParam = ''
        if (this.financeTypeId && (this.financeTypeId > 0)) {
            financeTypeParam = `&financeType=${this.financeTypeId}`
        }

        let rcptParam = ''
        if (this.receiptNumber && (this.receiptNumber.length > 0)) {
            rcptParam = `&number=${this.receiptNumber}`
        }

        let spanParam = '&span=month'
        if (contactParam && (contactParam.length > 0)) {
            spanParam = '&ipp=9'
        }

        this.loading = true
        if (this.page === 1) {
            this.payments = []
        }

        this.master.get(`payments/actual?page=${this.page}&sort=posted|DESC${lotParam}${contactParam}${financeTypeParam}${rcptParam}${spanParam}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.payments = this.payments.concat(res.data.payments)
                return
            }
  
            this.payments = res.data.payments
        })
    }

    protected icon = (category: string) => {
        if (!category || (category.length <= 0)) {
            return 'monetization_on'
        }

        switch(category.toLowerCase()) {
            case 'mv loan':
                return 'directions_car'
            case 'service':
                return 'car_repair'
            case 'deposit':
            case 'initial down':
            case 'deferred down':
                return 'payments'
            default:
                return 'monetization_on'
        }
    }

    /**
     * A payment in the list was clicked. 
     * @param e The event meta that trigger the payment selection.
     * @param payment The payment object that was chosen by the user.
     */
    protected selected = (e: Event, payment: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!payment || !payment['contactId']) {
            this.receipt(e, payment)
            return
        }

        this.router.navigateByUrl(`/collections/${payment['contactId']}?pay=${payment['id']}`)
    }

    /**
     * View a receipt for the selected payment.
     * @param e The event meta that triggered the receipt view
     * @param payment The payment object that was chosen by the user.
     */
    protected receipt = (e: Event, payment: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!payment || !payment['receiptId']) {
            return
        }

        this.receiptId = parseInt(payment['receiptId'])
    }

    /**
     * Listen for the user to close the receipt modal
     * @param {boolean} refunded Indicates whether or not the user decided to refund, part or whole, the payment.
     */
    protected receiptClosed = (refunded: boolean) => {
        if (refunded) {
            this.refundProcessed.emit(this.receiptId)

            this.reset()
            this.fetch()
        }

        this.receiptId = 0
    }
}
